// Copyright (c) Docugami, Inc. All rights reserved.

import { createWorkPathActions } from "components/work_path/WorkPathsHelper";
import { useNavigationService } from "hooks/navigationService";
import { ActionTile, ActionTileProps } from "./ActionTiles";
import styled from 'styled-components';
import { ActionTileId, useLimitsCtaTiles, useDocumentManagementTiles } from "hooks/ActionTileItems";
import { PageSection, SectionProps } from "./PageSections";
import { AutomationId } from "utils/Automation";
import { useEffect, useMemo, useRef, useState } from "react";
import { homeGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { IButtonStyles, IconButton } from "@fluentui/react";

const ActionTilesContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    margin: 20px 0;
    padding: 15px 5px 5px;
    box-sizing: border-box;
    overflow: hidden;
`;

const ScrollableContainer = styled.div`
    display: block;
    overflow-y: hidden;
    width: 100%;
`;

export const ActionsSection = (props: SectionProps): JSX.Element => {
    const [firstTileIndex, setFirstTileIndex] = useState(0);
    const [numTilesPerPage, setNumTilesPerPage] = useState(0);
    const sectionRef = useRef<HTMLDivElement>(null);
    const navigationService = useNavigationService();

    const limitsTiles = useLimitsCtaTiles();
    const docManagementTiles = useDocumentManagementTiles();
    const workPathActionsTiles = createWorkPathActions(props.types, navigationService, true);

    // The ActionTileId enum is ordered in the way we want to display the action tiles
    const orderedTileProps: ActionTileProps[] = useMemo(() => {
        const res: ActionTileProps[] = [];
        Object.values(ActionTileId).forEach(id => {
            const item = limitsTiles[id] || docManagementTiles[id] || workPathActionsTiles[id];
            if (!!item) {
                res.push(item);
            }
        });
        return res;
    }, [limitsTiles, docManagementTiles, workPathActionsTiles]);

    function updateTileDisplayInfo() {
        if (!sectionRef.current) {
            return;
        }

        const sectionRect = sectionRef.current.getBoundingClientRect();
        setNumTilesPerPage(Math.floor(sectionRect.width / 200));
    }

    useEffect(() => {
        setFirstTileIndex(0);
    }, [props.types])

    // Set the tile display info on mount
    useEffect(() => {
        updateTileDisplayInfo();
    }, [])

    // Add a window resize listener to update the tile display info
    useEffect(() => {
        window.addEventListener("resize", updateTileDisplayInfo);
        return () => window.removeEventListener("resize", updateTileDisplayInfo);
    });

    function getNavigationTool() {
        const totalNumTiles = orderedTileProps.length;

        const leftNavDisabled = firstTileIndex === 0;
        const rightNavDisabled = (totalNumTiles - firstTileIndex) <= numTilesPerPage;

        function navRight() {
            setFirstTileIndex(v => Math.min(v + numTilesPerPage, totalNumTiles - 1));
        }

        function navLeft() {
            setFirstTileIndex(v => Math.max(v - numTilesPerPage, 0));
        }

        return <TilesNav navRight={rightNavDisabled ? undefined : navRight} navLeft={leftNavDisabled ? undefined : navLeft} />
    }

    return (
        <PageSection title='Actions' automationId={AutomationId.HomeActionsPanel} commands={getNavigationTool()}>
            <ScrollableContainer ref={sectionRef}>
                <ActionTilesContainer>
                    {orderedTileProps.map((props, index) => {
                        // Skip rendering tiles before the current pointer
                        if (index < firstTileIndex) {
                            return null;
                        }
                        return <ActionTile key={props.id} {...props} isPageLimited={!!docManagementTiles[props.id]} isLimited={!!workPathActionsTiles[props.id] } />
                    })}
                </ActionTilesContainer>
            </ScrollableContainer>
        </PageSection>
    )
}

const NavContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: auto;
`;

const navButtonStyles: IButtonStyles = {
    icon: {
        color: homeGray
    },
    iconHovered: {
        fontWeight: 600
    },
    rootHovered: {
        backgroundColor: primaryTheme.palette.neutralLight
    },
    rootPressed: {
        backgroundColor: primaryTheme.palette.neutralQuaternaryAlt
    },
    rootDisabled: {
        opacity: .2
    }
}

interface TilesNavProps {
    navRight?: () => void;
    navLeft?: () => void;
}

export const TilesNav = (props: TilesNavProps): JSX.Element => (
    <NavContainer>
        <IconButton
            title="Left Navigation"
            iconProps={{ iconName: "ChevronLeftMed" }}
            styles={navButtonStyles}
            onClick={props.navLeft}
            disabled={!props.navLeft} />
        <IconButton
            title="Right Navigation"
            iconProps={{ iconName: "ChevronRightMed" }}
            styles={navButtonStyles}
            onClick={props.navRight}
            disabled={!props.navRight} />
    </NavContainer>
)
